// lib
import { useContext, useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/components/context'

import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'
import useApplyFilters from '@aytm/app/stats/page/sidebar/components/hooks/useApplyFilters'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import { isFilterApplied } from '@aytm/app/stats/page/components/lib/utils'
import { toggleCheckbox } from '@aytm/app/stats/page/sidebar/components/lib/utils'

// components
import { Button } from 'primereact/button'
import { RadioButton } from 'primereact/radiobutton'
import { Checkbox } from 'primereact/checkbox'

// styles
import style from '@aytm/app/stats/page/sidebar/export/styles/ExportPanel.module.scss'

const Summary = ({ survey, model, report, features, isOeExists, processing, disabled, onExport, onParamsChange }) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  // The stats service has no CSV summary writer (it returns an empty file), so Excel is the only format.
  const [fileType, setFileType] = useState('excel')

  const [clExportVisibility, setClExportVisibility] = useState('hide' == model.clExportVisibility)
  const [ignoreTags, setIgnoreTags] = useState(model.ignoreTagsExport)

  const [applyFilters, setApplyFilters] = useApplyFilters(sidebarState.export?.summary, model.filtersPattern)
  const [dataQualityExport, setDataQualityExport] = useState(
    Object.hasOwn(sidebarState, 'export') && Object.hasOwn(sidebarState.export.summary, 'dataQualityExport')
      ? sidebarState.export.summary.dataQualityExport
      : model.dataQualityExport
  )

  const onExportClick = useCallback(
    event => {
      'function' == typeof onExport &&
        onExport({
          fileType,
          exportType: 'summary',
          props: { noCache: Boolean(event.shiftKey), clExportVisibility, applyFilters },
          options: { ignoreTags, dataQualityExport }
        })
    },
    [fileType, clExportVisibility, applyFilters, ignoreTags, dataQualityExport, onExport]
  )

  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'export',
        state: {
          summary: {
            ...sidebarState.export.summary,
            fileType,
            applyFilters,
            // the filter the box was set against, so a later open keeps a hand-set tick only under it
            filtersPattern: model.filtersPattern,
            dataQualityExport
          }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileType, applyFilters, model.filtersPattern, dataQualityExport]
  )

  useNonInitialEffect(
    () =>
      onParamsChange(
        {
          cl_export_visibility: clExportVisibility ? 'hide' : 'show',
          ignore_tags_export: ignoreTags
        },
        false
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clExportVisibility, ignoreTags, onParamsChange]
  )

  // useEffect(() => {}, [sidebarState.export.summary])

  return (
    <>
      <div className={style.section}>
        <div className={style['section-title']}>
          <b>File types</b>
        </div>

        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-12`}>
            <RadioButton
              data-testid="stat-exports-summary-excel"
              className="p-radiobutton-sm"
              aria-labelledby="stat-exports-summary-excel-label"
              value="excel"
              checked={'excel' == fileType}
              disabled={disabled}
              onChange={disabled ? null : () => setFileType('excel')}
            />
            <span
              id="stat-exports-summary-excel-label"
              data-testid="stat-exports-summary-excel-label"
              className={classNames(style['row-label'], style['row-label-sm'], {
                'p-disabled': disabled
              })}
              onClick={disabled ? null : () => setFileType('excel')}
            >
              Excel
            </span>
          </div>
        </div>
      </div>

      <div className={style.section}>
        {survey?.cl_exists ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-summary-cl-export-visibility"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-summary-cl-export-visibility-label"
                value="cl-export-visibility"
                checked={clExportVisibility}
                disabled={disabled}
                onChange={disabled ? null : () => setClExportVisibility(!clExportVisibility)}
                onKeyDown={
                  disabled ? null : event => toggleCheckbox(event, () => setClExportVisibility(!clExportVisibility))
                }
              />
              <span
                id="stat-exports-summary-cl-export-visibility-label"
                data-testid="stat-exports-summary-cl-export-visibility-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setClExportVisibility(!clExportVisibility)}
              >
                Hide aytm Logic
              </span>
            </div>
          </div>
        ) : null}
        {isFilterApplied(model.filtersPattern) ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-summary-apply-filters"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-summary-apply-filters-label"
                value="apply-filters"
                checked={applyFilters}
                disabled={disabled}
                onChange={disabled ? null : () => setApplyFilters(!applyFilters)}
                onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setApplyFilters(!applyFilters))}
              />
              <span
                id="stat-exports-summary-apply-filters-label"
                data-testid="stat-exports-summary-apply-filters-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setApplyFilters(!applyFilters)}
              >
                Apply active filters
              </span>
            </div>
          </div>
        ) : null}
        {isOeExists ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-summary-ignore-tags"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-summary-ignore-tags-label"
                value="ignore-tags"
                checked={ignoreTags}
                disabled={disabled}
                onChange={disabled ? null : () => setIgnoreTags(!ignoreTags)}
                onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setIgnoreTags(!ignoreTags))}
              />
              <span
                id="stat-exports-summary-ignore-tags-label"
                data-testid="stat-exports-summary-ignore-tags-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setIgnoreTags(!ignoreTags)}
              >
                Exclude OE Code Columns
              </span>
            </div>
          </div>
        ) : null}
        {!PageGlobals.USER_ID ||
        (PageGlobals.SHARE_KEY != undefined && PageGlobals.SHARE_KEY.key != undefined) ||
        features.data_quality_export.disabled ? null : (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-summary-data-quality-export"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-summary-data-quality-export-label"
                value="data-quality-export"
                checked={dataQualityExport}
                disabled={disabled}
                onChange={disabled ? null : () => setDataQualityExport(!dataQualityExport)}
                onKeyDown={
                  disabled ? null : event => toggleCheckbox(event, () => setDataQualityExport(!dataQualityExport))
                }
              />
              <span
                id="stat-exports-summary-data-quality-export-label"
                data-testid="stat-exports-summary-data-quality-export-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setDataQualityExport(!dataQualityExport)}
              >
                Respondent Detail Export
              </span>
            </div>
          </div>
        )}
      </div>

      <div className={style.section}>
        <div className={style.row}>
          <Button
            data-testid="stat-exports-summary-export"
            className="p-button-rounded p-button-blue"
            label={'summary' == processing ? 'Downloading' : 'Download file'}
            loading={'summary' == processing}
            disabled={null !== processing || disabled}
            onClick={null !== processing || disabled ? null : onExportClick}
          />
        </div>

        {!report.current.schema.length ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least 1 question in order to use these features
          </div>
        ) : null}
      </div>
    </>
  )
}

Summary.propTypes = {
  survey: PropTypes.object,
  model: PropTypes.object,
  report: PropTypes.object,
  features: PropTypes.object,
  isOeExists: PropTypes.bool,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func,
  onParamsChange: PropTypes.func
}

Summary.displayName = 'Summary'

export default Summary
